import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { PenTool, MonitorPlay, Send, Server, LineChart } from 'lucide-react';
interface SlideSnakkerPlatformProps {
  onContinue: () => void;
}
export function SlideSnakkerPlatform({
  onContinue
}: SlideSnakkerPlatformProps) {
  const [stage, setStage] = useState(0);
  const cards = [{
    icon: <PenTool className="w-7 h-7" data-id="element-552" />,
    label: 'Creation',
    sub: 'Engine'
  }, {
    icon: <MonitorPlay className="w-7 h-7" data-id="element-553" />,
    label: 'Interactive',
    sub: 'Player'
  }, {
    icon: <Send className="w-7 h-7" data-id="element-554" />,
    label: 'Publishing',
    sub: '',
    highlighted: true
  }, {
    icon: <Server className="w-7 h-7" data-id="element-555" />,
    label: 'Streaming',
    sub: 'Runtime'
  }, {
    icon: <LineChart className="w-7 h-7" data-id="element-556" />,
    label: 'Data &',
    sub: 'Optimization'
  }];
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 400);
    const t2 = setTimeout(() => setStage(2), 1100); // line draws
    const timers = cards.map((_, idx) => setTimeout(() => setStage(3 + idx), 1500 + idx * 250));
    const finalTimer = setTimeout(() => setStage(3 + cards.length), 1500 + cards.length * 250 + 300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      timers.forEach(clearTimeout);
      clearTimeout(finalTimer);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-557">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 20
    }} data-id="element-558">
        <h2 className="text-4xl md:text-6xl font-sans font-bold text-white tracking-tight leading-tight" data-id="element-559">
          The Snakker platform
        </h2>
        <motion.div initial={{
        width: 0
      }} animate={{
        width: stage >= 1 ? 80 : 0
      }} transition={{
        duration: 0.5
      }} className="h-[3px] bg-blue-400 rounded-full mt-3" data-id="element-560" />
      </motion.div>

      {/* Pipeline */}
      <div className="flex-1 flex items-center justify-center" data-id="element-561">
        <div className="relative w-full max-w-6xl" data-id="element-562">
          {/* Connecting horizontal line */}
          <div className="absolute top-1/2 left-[8%] right-[8%] h-px bg-navy-600 -translate-y-1/2" data-id="element-563" />
          <motion.div initial={{
          scaleX: 0
        }} animate={{
          scaleX: stage >= 2 ? 1 : 0
        }} transition={{
          duration: 0.9,
          ease: 'easeInOut'
        }} style={{
          transformOrigin: 'left'
        }} className="absolute top-1/2 left-[8%] right-[8%] h-px bg-blue-400/40 -translate-y-1/2" data-id="element-564" />

          {/* Cards */}
          <div className="grid grid-cols-5 gap-4 relative z-10" data-id="element-565">
            {cards.map((card, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20,
            scale: 0.9
          }} animate={{
            opacity: stage >= 3 + idx ? 1 : 0,
            y: stage >= 3 + idx ? 0 : 20,
            scale: stage >= 3 + idx ? 1 : 0.9
          }} transition={{
            duration: 0.5
          }} className={`
                  bg-navy-900/70 backdrop-blur-sm border rounded-2xl flex flex-col items-center justify-center text-center
                  ${card.highlighted ? 'border-blue-400/40 shadow-[0_0_30px_rgba(96,165,250,0.15)] aspect-[5/6] p-6 scale-110' : 'border-navy-600 aspect-square p-5'}
                `} data-id="element-566">
                <div className={`text-blue-400 mb-4 ${card.highlighted ? '' : ''}`} data-id="element-567">
                  {card.icon}
                </div>
                <div className="text-white font-bold leading-tight text-base md:text-lg" data-id="element-568">
                  {card.label}
                  {card.sub && <>
                      <br data-id="element-569" />
                      {card.sub}
                    </>}
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 3 + cards.length ? 1 : 0
    }} className="mt-8 flex justify-end" data-id="element-570">
        <Button onClick={onContinue} data-id="element-571">Flywheel</Button>
      </motion.div>
    </div>;
}